import React, { useEffect, useState, useCallback, useRef } from "react";
import { Trophy, Timer } from "lucide-react";
import { api } from "../lib/api";

const PERIODS = [
  { k: "today", l: "Oggi" },
  { k: "week", l: "Sett." },
  { k: "month", l: "Mese" },
  { k: "all", l: "Sempre" },
];

function fmtDur(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return s === 0 ? "0 min" : "<1 min";
  const totalMin = Math.floor(s / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m} min`;
}

const RANK_COLORS = ["#C9A227", "#9AA3AE", "#B08D57"]; // oro, argento, bronzo

export default function AdminActivityLeaderboard() {
  const [period, setPeriod] = useState("today");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async (p) => {
    try {
      const { data } = await api.get(`/admin/activity/leaderboard?period=${p}`);
      setEntries(data.entries || []);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load(period);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load(period), 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [period, load]);

  return (
    <div
      className="rounded-xl border border-ink/10 bg-white/70 backdrop-blur px-3.5 py-2.5 shadow-sm w-full"
      data-testid="activity-leaderboard-card"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="flex items-center gap-1.5 text-ink flex-shrink-0"
          title="Tempo di utilizzo effettivo (mouse, tastiera, click) nell'app"
        >
          <Timer className="w-4 h-4 text-gold" />
          <span className="text-[11px] font-display uppercase tracking-[0.12em]">Classifica attività</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1" data-testid="activity-leaderboard-list">
          {loading && entries.length === 0 ? (
            <span className="text-[12px] text-slate1">Caricamento…</span>
          ) : entries.length === 0 ? (
            <span className="text-[12px] text-slate1">Nessun admin</span>
          ) : (
            entries.map((e) => {
              const isTop = e.rank === 1 && (e.seconds || 0) > 0;
              const medal = e.rank <= 3 ? RANK_COLORS[e.rank - 1] : null;
              return (
                <div
                  key={e.user_id}
                  className={`flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 border ${
                    e.is_me ? "border-gold/60 bg-gold/10" : "border-ink/10 bg-ink/[0.02]"
                  }`}
                  data-testid={`activity-row-${e.rank}`}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {isTop ? (
                      <Trophy className="w-4 h-4" style={{ color: RANK_COLORS[0] }} data-testid="activity-trophy" />
                    ) : (
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold"
                        style={medal ? { color: medal, border: `1.5px solid ${medal}` } : { color: "#8a93a0", border: "1.5px solid #e2e5e9" }}
                      >
                        {e.rank}
                      </span>
                    )}
                  </span>
                  <span className="text-[12.5px] font-semibold text-ink truncate max-w-[150px]">
                    {e.name}
                    {e.is_me ? <span className="text-[10px] text-gold font-normal ml-0.5">(tu)</span> : null}
                  </span>
                  <span className="text-[12.5px] font-bold text-ink tabular-nums" data-testid={`activity-minutes-${e.rank}`}>
                    {fmtDur(e.seconds)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="inline-flex rounded-full bg-ink/5 p-0.5 flex-shrink-0" data-testid="activity-period-toggle">
          {PERIODS.map((p) => (
            <button
              key={p.k}
              onClick={() => setPeriod(p.k)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
                period === p.k ? "bg-ink text-cream" : "text-slate1 hover:text-ink"
              }`}
              data-testid={`activity-period-${p.k}`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
